import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/axiosInstance';
import { useMonitoring } from '../context/MonitoringContext';
import DataGridList from '../components/grid/DataGridList';
import {
    renderGroupLabel, calculateGroupLabel,
    renderSqlVersion, calculateSqlVersion,
    makeVersionCell, renderTypeBadge, renderStatusBadge,
    applyRowStatusClass,
} from '../utils/columnRenderers';

export default function Dashboard() {
    const {
        servers,
        loadingServers,
        hoPkg,
        dsPkg,
        ssPkg,
    } = useMonitoring();

    const [serverTypes, setServerTypes] = useState([]);

    useEffect(() => {
        api.get("/servertypes")
            .then(res => {
                setServerTypes(res.data || []);
            })
            .catch(err => console.error("Failed to load server types", err));
    }, []);

    // Helpers to get stats per server type
    const getFleetStatsForType = useCallback((typeName) => {
        const typeServers = servers.filter(s => (s.serverType || "Live").toLowerCase() === typeName.toLowerCase());
        const online = typeServers.filter(s => s.status?.toLowerCase() === "online").length;
        return { online, total: typeServers.length };
    }, [servers]);

    const getHoStatsForType = useCallback((typeName) => {
        const typeServers = servers.filter(s => (s.serverType || "Live").toLowerCase() === typeName.toLowerCase());
        const updated = typeServers.filter(s => s.hoVersion && s.targetHoVersion && s.hoVersion === s.targetHoVersion).length;
        return { updated, total: typeServers.length };
    }, [servers]);

    const getDsStatsForType = useCallback((typeName) => {
        const typeServers = servers.filter(s => (s.serverType || "Live").toLowerCase() === typeName.toLowerCase());
        const updated = typeServers.filter(s => s.dsVersion && s.targetDsVersion && s.dsVersion === s.targetDsVersion).length;
        return { updated, total: typeServers.length };
    }, [servers]);

    const getProgressStatsForType = useCallback((typeName) => {
        const typeServers = servers.filter(s => (s.serverType || "Live").toLowerCase() === typeName.toLowerCase());
        const running = typeServers.filter(s => {
            if (!s.status) return false;
            const st = s.status.toLowerCase();
            return st !== 'online' && st !== 'offline';
        }).length;
        return { running, total: typeServers.length };
    }, [servers]);

    // Overview list: read-only, search only. No CRUD or actions (CRUD happens on Servers page).
    // Columns: group, sql version (formatted to e.g. "SQL Server 2019"), HO, DS, Type, status
    const overviewColumns = useMemo(() => [
        {
            dataField: "displayName",
            caption: "Group",
            sortOrder: "asc",
            calculateCellValue: calculateGroupLabel,
            calculateDisplayValue: calculateGroupLabel,
            cellRender: renderGroupLabel
        },
        {
            dataField: "sqlVersion",
            caption: "SQL Version",
            calculateCellValue: calculateSqlVersion,
            calculateDisplayValue: calculateSqlVersion,
            cellRender: renderSqlVersion
        },
        {
            dataField: "hoVersion",
            caption: "HO",
            cellRender: makeVersionCell("hoVersion", "targetHoVersion")
        },
        {
            dataField: "dsVersion",
            caption: "DS",
            cellRender: makeVersionCell("dsVersion", "targetDsVersion")
        },
        {
            dataField: "serverType",
            caption: "Type",
            width: 90,
            cellRender: renderTypeBadge
        },
        {
            dataField: "status",
            caption: "Status",
            width: 100,
            cellRender: renderStatusBadge
        }
    ], []);

    const handleRowPrepared = useCallback(applyRowStatusClass, []);

    return (
        <>
            {/* Dashboard Header */}
            <header className="dashboard-header">
                <div className="dashboard-title-area">
                    <h1>Groups Overview</h1>
                    <p>Monitor real-time group telemetry, SQL versions, and component statuses.</p>
                </div>
                <div className="dashboard-target-versions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                            Target Versions
                        </span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <div style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)', fontWeight: '600', border: '1px solid var(--border-color)', background: 'var(--content-bg)', color: 'var(--text-color)' }}>
                                HO: <span style={{ color: 'var(--brand-light)' }}>{hoPkg?.version || '1.0.0.0'}</span>
                            </div>
                            <div style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)', fontWeight: '600', border: '1px solid var(--border-color)', background: 'var(--content-bg)', color: 'var(--text-color)' }}>
                                DS: <span style={{ color: 'var(--brand-light)' }}>{dsPkg?.version || '1.0.0.0'}</span>
                            </div>
                            <div style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)', fontWeight: '600', border: '1px solid var(--border-color)', background: 'var(--content-bg)', color: 'var(--text-color)' }}>
                                SS: <span style={{ color: 'var(--brand-light)' }}>{ssPkg?.version || '1.0.0.0'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* KPI cards - All split by Server Types */}
            <div className="kpi-grid">
                {/* Card 1: Servers Online */}
                <div className="kpi-card" style={{ alignItems: 'flex-start' }}>
                    <div className="kpi-icon-wrap" style={{ marginTop: '2px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                            <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                            <line x1="6" y1="6" x2="6.01" y2="6" />
                            <line x1="6" y1="18" x2="6.01" y2="18" />
                        </svg>
                    </div>
                    <div className="kpi-info" style={{ width: '100%', minWidth: 0, paddingRight: '4px' }}>
                        <span className="kpi-label" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', color: 'var(--text-muted)' }}>
                            Servers Online
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {serverTypes.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
                            ) : (
                                serverTypes.map((st, index) => {
                                    const stats = getFleetStatsForType(st.name);
                                    return (
                                        <React.Fragment key={st.id || st.name}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="kpi-label" style={{ margin: 0, textTransform: 'uppercase' }}>{st.name}</span>
                                                <span className="kpi-val" style={{ fontSize: '18px' }}>
                                                    {stats.online} <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>/ {stats.total}</span>
                                                </span>
                                            </div>
                                            {index < serverTypes.length - 1 && (
                                                <div style={{ height: '1px', background: 'var(--border-color)', opacity: 0.5 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 2: HeadOffice Updated */}
                <div className="kpi-card" style={{ alignItems: 'flex-start' }}>
                    <div className="kpi-icon-wrap" style={{ color: 'var(--success)', background: 'var(--success-soft)', marginTop: '2px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <div className="kpi-info" style={{ width: '100%', minWidth: 0, paddingRight: '4px' }}>
                        <span className="kpi-label" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', color: 'var(--text-muted)' }}>
                            HeadOffice Updated
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {serverTypes.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
                            ) : (
                                serverTypes.map((st, index) => {
                                    const stats = getHoStatsForType(st.name);
                                    const isAllUpdated = stats.total > 0 && stats.updated === stats.total;
                                    return (
                                        <React.Fragment key={st.id || st.name}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="kpi-label" style={{ margin: 0, textTransform: 'uppercase' }}>{st.name}</span>
                                                <span className="kpi-val" style={{ fontSize: '18px', color: isAllUpdated ? 'var(--success)' : 'var(--text-color)' }}>
                                                    {stats.updated} <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>/ {stats.total}</span>
                                                </span>
                                            </div>
                                            {index < serverTypes.length - 1 && (
                                                <div style={{ height: '1px', background: 'var(--border-color)', opacity: 0.5 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 3: DataServices Updated */}
                <div className="kpi-card" style={{ alignItems: 'flex-start' }}>
                    <div className="kpi-icon-wrap" style={{ color: 'var(--success)', background: 'var(--success-soft)', marginTop: '2px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <div className="kpi-info" style={{ width: '100%', minWidth: 0, paddingRight: '4px' }}>
                        <span className="kpi-label" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', color: 'var(--text-muted)' }}>
                            DataServices Updated
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {serverTypes.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
                            ) : (
                                serverTypes.map((st, index) => {
                                    const stats = getDsStatsForType(st.name);
                                    const isAllUpdated = stats.total > 0 && stats.updated === stats.total;
                                    return (
                                        <React.Fragment key={st.id || st.name}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="kpi-label" style={{ margin: 0, textTransform: 'uppercase' }}>{st.name}</span>
                                                <span className="kpi-val" style={{ fontSize: '18px', color: isAllUpdated ? 'var(--success)' : 'var(--text-color)' }}>
                                                    {stats.updated} <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>/ {stats.total}</span>
                                                </span>
                                            </div>
                                            {index < serverTypes.length - 1 && (
                                                <div style={{ height: '1px', background: 'var(--border-color)', opacity: 0.5 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 4: Updates In Progress */}
                <div className="kpi-card" style={{ alignItems: 'flex-start' }}>
                    <div className="kpi-icon-wrap" style={{ color: 'var(--info)', background: 'var(--info-soft)', marginTop: '2px' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </div>
                    <div className="kpi-info" style={{ width: '100%', minWidth: 0, paddingRight: '4px' }}>
                        <span className="kpi-label" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', color: 'var(--text-muted)' }}>
                            Updates In Progress
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {serverTypes.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)' }}>Loading...</span>
                            ) : (
                                serverTypes.map((st, index) => {
                                    const stats = getProgressStatsForType(st.name);
                                    return (
                                        <React.Fragment key={st.id || st.name}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="kpi-label" style={{ margin: 0, textTransform: 'uppercase' }}>{st.name}</span>
                                                <span className="kpi-val" style={{ fontSize: '18px', color: stats.running > 0 ? 'var(--warning)' : 'var(--text-color)' }}>
                                                    {stats.running} <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>/ {stats.total}</span>
                                                </span>
                                            </div>
                                            {index < serverTypes.length - 1 && (
                                                <div style={{ height: '1px', background: 'var(--border-color)', opacity: 0.5 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Overview Table */}
            <div className="portal-stack">
                <section className="portal-panel">
                    <div style={{ height: "auto", width: "100%" }}>
                        <DataGridList
                            data={servers}
                            keyExpr="serverId"
                            columns={overviewColumns}
                            height="auto"
                            toolbarVisible={true}
                            gridTitle="Active Groups"
                            showGridTitle={true}
                            filterButtonVisible={false}
                            showSearchPanel={true}
                            showHeaderFilter={false}
                            showFilterRow={false}
                            showPager={true}
                            pageSize={10}
                            focusedRowEnabled={false}
                            selectionMode="none"
                            onRowPrepared={handleRowPrepared}
                            isLoading={loadingServers}
                        />
                    </div>
                </section>
            </div>
        </>
    );
}